import { z } from "zod";

export const CREDENTIAL_CATEGORIES = [
  "license",
  "certification",
  "insurance",
  "training",
  "identification",
  "other",
] as const;

export const CREDENTIAL_STATUSES = [
  "valid",
  "expiring",
  "expired",
  "no_expiry",
] as const;

export const CREDENTIAL_STATUS_FILTERS = [
  { id: "all", label: "All statuses" },
  { id: "valid", label: "Valid" },
  { id: "expiring", label: "Expiring" },
  { id: "expired", label: "Expired" },
  { id: "no_expiry", label: "No expiry" },
] as const;

export type CredentialStatusFilterId =
  (typeof CREDENTIAL_STATUS_FILTERS)[number]["id"];

export const CREDENTIAL_PARSE_STATUSES = [
  "pending",
  "parsed",
  "failed",
  "confirmed",
] as const;

export const CREDENTIAL_CHANNELS = ["email", "push", "both"] as const;

export const CREDENTIAL_CATEGORY_LABELS: Record<
  (typeof CREDENTIAL_CATEGORIES)[number],
  string
> = {
  license: "License",
  certification: "Certification",
  insurance: "Insurance",
  training: "Training",
  identification: "ID",
  other: "Other",
};

export const CREDENTIAL_MAX_REMINDERS = 8;

export const CREDENTIAL_DEFAULT_REMINDERS: {
  daysBefore: number;
  channel: (typeof CREDENTIAL_CHANNELS)[number];
}[] = [
  { daysBefore: 30, channel: "both" },
  { daysBefore: 10, channel: "both" },
  { daysBefore: 1, channel: "both" },
];

export const CredentialReminderSchema = z.object({
  id: z.string().optional(),
  daysBefore: z.number().int(),
  channel: z.enum(CREDENTIAL_CHANNELS),
  position: z.number().int().optional(),
});

export type CredentialReminder = z.infer<typeof CredentialReminderSchema>;

export const CredentialShareSchema = z.object({
  id: z.string(),
  token: z.string(),
  url: z.string(),
  expiresAt: z.string().nullable().optional(),
  revokedAt: z.string().nullable().optional(),
  active: z.boolean(),
});

export type CredentialShare = z.infer<typeof CredentialShareSchema>;

export const CredentialSchema = z.object({
  id: z.string(),
  userId: z.string(),
  name: z.string().nullable().optional(),
  issuer: z.string().nullable().optional(),
  credentialNumber: z.string().nullable().optional(),
  category: z.enum(CREDENTIAL_CATEGORIES).or(z.string()),
  issuedOn: z.string().nullable().optional(),
  expiresOn: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  parseStatus: z.enum(CREDENTIAL_PARSE_STATUSES).or(z.string()),
  status: z.enum(CREDENTIAL_STATUSES).or(z.string()),
  holderName: z.string().nullable().optional(),
  thumbnailUrl: z.string().nullable().optional(),
  imageUrl: z.string().nullable().optional(),
  reminders: z.array(CredentialReminderSchema).optional(),
  shares: z.array(CredentialShareSchema).optional(),
  confirmedAt: z.string().nullable().optional(),
  parsedPayload: z.unknown().optional(),
});

export type Credential = z.infer<typeof CredentialSchema>;

export const CredentialUploadUrlSchema = z.object({
  uploadUrl: z.string(),
  storageKey: z.string(),
  credentialId: z.string(),
  imageId: z.string(),
  userId: z.string(),
});

export type CredentialUploadUrl = z.infer<typeof CredentialUploadUrlSchema>;
